import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import Swal from "sweetalert2";
import introDesktopGif from "./img/Company_IntroScene.gif";
import introMobileGif from "./img/Mobile_Intro.gif";

// old/index.php auto-forwarded a logged-in player to welcome.php after 5s.
const AUTO_BEGIN_DELAY_MS = 5000;

/** Mirrors old/index.php's right-click/devtools-shortcut block. */
function useDisableInspect() {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockShortcutKeys = (e) => {
      if (e.keyCode === 123) return false; // F12
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) return false; // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) return false; // Ctrl+Shift+J
      if (e.ctrlKey && e.keyCode === 85) return false; // Ctrl+U
      return true;
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockShortcutKeys);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockShortcutKeys);
    };
  }, []);
}

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const [beginLoading, setBeginLoading] = useState(false);
  const pageRef = useRef(null);

  useDisableInspect();

  // The page sits under the shared header; size it to exactly the space that
  // is left (see .login-page) so the gif + button never cause a page scroll.
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const sync = () => {
      const top = page.getBoundingClientRect().top + window.scrollY;
      page.style.setProperty("--login-top", `${Math.max(0, Math.round(top))}px`);
    };
    sync();
    const header = page.previousElementSibling;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (ro && header) ro.observe(header);
    window.addEventListener("resize", sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, []);

  const canBegin =
    status === "succeeded" && Boolean(user) && Number(user?.gameover ?? 0) <= 0;

  // old/index.php's BEGIN PLAY went straight to welcome.php (the wish form).
  const handleBeginPlay = useCallback(
    (e) => {
      e?.preventDefault();
      if (!canBegin || beginLoading) return;
      setBeginLoading(true);
      navigate("/welcome", { replace: true });
    },
    [canBegin, beginLoading, navigate]
  );

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  // old/index.php: once logged in, move on to welcome.php after 5 seconds
  // even if BEGIN PLAY isn't clicked.
  useEffect(() => {
    if (isAdmin || !canBegin) return undefined;
    const t = window.setTimeout(() => {
      navigate("/welcome", { replace: true });
    }, AUTO_BEGIN_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [isAdmin, canBegin, navigate]);

  // old/index.php used a plain white page (body.bg2) behind the intro gif.
  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundColor: body.style.backgroundColor,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = "none";
    body.style.backgroundColor = "#ffffff";
    return () => {
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundColor = prev.backgroundColor;
    };
  }, []);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // show "already played" when login payload has gameover > 0 (DB stages.gameover)
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (Number(user.gameover ?? 0) > 0) {
      const redirect = user.backButtonRedirect || "/";
      Swal.fire("You have already played!", "  ", "error").then(() => {
        window.location.href = redirect;
      });
    }
  }, [status, user, error, isAdmin]);

  return (
    <div className="login-page" ref={pageRef}>
      <div className="login-intro">
        <img
          src={introDesktopGif}
          className="login-intro-desk"
          alt="My Company Plays Santa"
        />
        <img
          src={introMobileGif}
          className="login-intro-mob"
          alt="My Company Plays Santa"
        />
      </div>

      {!isAdmin ? (
        <button
          type="button"
          className="begin-play-btn"
          disabled={beginLoading || !canBegin}
          onClick={handleBeginPlay}
        >
          {beginLoading ? "LOADING…" : "BEGIN PLAY"}
        </button>
      ) : null}
    </div>
  );
}

export default Login;
